import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";

import branchesService from "../api/branchesService";
import pestReportService from "../api/pestReportService";
import servicesService from "../api/servicesService";
import GalleryRow from "../components/GalleryRow";
import { ViewPestReportModal } from "./Calendario/components/ViewPestReportModal";
import {
  formatServiceDateShort,
  isServiceDateBeforeToday,
  isServiceDateOnOrAfterToday,
} from "../utils/dateUtils";

// ─── Icons (inline SVG para no depender de MUI Icons) ─────────────────────
const Icon = {
  Store: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
      <path d="M20 4H4v2l-2 6v2h1v6h10v-6h4v6h2v-6h1v-2l-2-6V4zm-8 14H6v-4h6v4zm2-8H4.48l1.33-4H18.2l1.33 4H14z"/>
    </svg>
  ),
  Bug: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      <path d="M20 8h-2.81c-.45-.78-1.07-1.45-1.82-1.96L17 4.41 15.59 3l-2.17 2.17C12.96 5.06 12.49 5 12 5s-.96.06-1.41.17L8.41 3 7 4.41l1.62 1.63C7.88 6.55 7.26 7.22 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2v1c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8zm-4 4v3c0 2.21-1.79 4-4 4s-4-1.79-4-4v-3c0-2.21 1.79-4 4-4s4 1.79 4 4z"/>
    </svg>
  ),
  Wrench: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
      <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
    </svg>
  ),
  Location: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  ),
  Chart: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
    </svg>
  ),
  History: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
    </svg>
  ),
  Report: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
    </svg>
  ),
  Image: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
    </svg>
  ),
  ExternalLink: () => (
    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
      <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
    </svg>
  ),
};

// ─── Helpers ──────────────────────────────────────────────────────────────
const getPestLevelColor = (level) => {
  const lvl = Number(level || 0);
  if (lvl === 0) return { bg: "#f1f5f9", text: "#64748b", label: "Nulo" };
  if (lvl === 1) return { bg: "#dcfce7", text: "#15803d", label: "Bajo" };
  if (lvl === 2) return { bg: "#fef9c3", text: "#a16207", label: "Medio" };
  if (lvl === 3) return { bg: "#fee2e2", text: "#b91c1c", label: "Crítico" };
  return { bg: "#f1f5f9", text: "#64748b", label: "—" };
};

const getStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "completado" || s === "completed")
    return { bg: "#dcfce7", text: "#15803d", label: "Completado" };
  if (s === "pendiente" || s === "pending")
    return { bg: "#dbeafe", text: "#1d4ed8", label: "Pendiente" };
  if (s === "en progreso" || s === "in progress")
    return { bg: "#fef3c7", text: "#d97706", label: "En progreso" };
  if (s === "cancelado" || s === "cancelled")
    return { bg: "#fee2e2", text: "#b91c1c", label: "Cancelado" };
  return { bg: "#f1f5f9", text: "#475569", label: status || "—" };
};

const fmtDate = (d) => formatServiceDateShort(d, "es-MX");

const fmtDateShort = (d) =>
  d
    ? new Date(d).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
      })
    : "—";

// ─── Custom Tooltip for Chart ──────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const lvl = payload[0]?.value ?? 0;
  const { bg, text, label: lvlLabel } = getPestLevelColor(lvl);
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 13,
      }}
    >
      <div style={{ color: "#64748b", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            background: bg,
            color: text,
            borderRadius: 4,
            padding: "2px 8px",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {lvlLabel}
        </span>
        <span style={{ fontWeight: 700, color: "#0f172a" }}>
          Nivel {lvl}
        </span>
      </div>
    </div>
  );
};

// ─── Spinner ──────────────────────────────────────────────────────────────
const Spinner = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        border: "3px solid #e2e8f0",
        borderTop: "3px solid #1975d1",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }}
    />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ─── Badge ────────────────────────────────────────────────────────────────
const Badge = ({ children, bg = "#dbeafe", color = "#1d4ed8" }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      background: bg,
      color,
      borderRadius: 6,
      padding: "3px 10px",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.01em",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

// ─── Section Header ───────────────────────────────────────────────────────
const SectionHeader = ({ icon: IconComp, title, subtitle, action }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: 20,
      gap: 12,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          background: "#eff6ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1975d1",
          flexShrink: 0,
        }}
      >
        <IconComp />
      </div>
      <div>
        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 1 }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
    {action}
  </div>
);

// ─── Card wrapper ─────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e8edf3",
      borderRadius: 14,
      padding: 24,
      ...style,
    }}
  >
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function Sucursal() {
  const { id } = useParams();

  const [branch, setBranch] = useState(null);
  const [reports, setReports] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => { loadData(); }, [id]);

  async function loadData() {
    try {
      setLoading(true);
      setError("");

      const branchRes = await branchesService.getById(id);
      const branchData = branchRes.data.data;
      setBranch(branchData);

      const servicesRes = await servicesService.getAll();
      const allServices = servicesRes?.data?.data || [];

      const branchServices = allServices
        .filter((s) =>
          s?.branchId === id ||
          s?.branch?._id === id ||
          s?.branch?.id === id
        )
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

      setServices(branchServices);

      const reportsResults = await Promise.all(
        branchServices.map(async (service) => {
          try {
            const res = await pestReportService.getReportsByEventId(
              service.eventId || service._id
            );
            // Attach the service to each report so we can open the correct modal
            const reports = Array.isArray(res) ? res : (res?.data?.data || []);
            return reports.map((r) => ({ ...r, _service: service }));
          } catch {
            return [];
          }
        })
      );

      const branchReports = reportsResults
        .flat()
        .filter(Boolean)
        .sort(
          (a, b) =>
            new Date(b.createdAt || b.date || 0) -
            new Date(a.createdAt || a.date || 0)
        );

      setReports(branchReports);
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar la sucursal.");
    } finally {
      setLoading(false);
    }
  }

  // ── Derived data ─────────────────────────────────────────────────────────
  //HARDCODED DATA
   
  const chartData = useMemo(
    () =>
      reports
        .slice(0, 20)
        .reverse()
        .map((r) => ({
          fecha: fmtDateShort(r.createdAt || r.date),
          nivel: Number(r.pestLevel || 0),
        })),
    [reports]
  );

  const latestReports = useMemo(() => reports.slice(0, 8), [reports]);

  const servicesHistory = useMemo(() => {
    return services.filter((s) => isServiceDateBeforeToday(s.date));
  }, [services]);

  const upcomingServices = useMemo(() => {
    return services
      .filter((s) => isServiceDateOnOrAfterToday(s.date))
      .slice(0, 3);
  }, [services]);

  const avgPestLevel = useMemo(() => {
    if (!reports.length) return 0;
    const sum = reports.reduce((acc, r) => acc + Number(r.pestLevel || 0), 0);
    return (sum / reports.length).toFixed(1);
  }, [reports]);

  const googleMapsUrl = useMemo(() => {
    const address = encodeURIComponent(branch?.address || "Ciudad de México");
    return `https://maps.google.com/maps?q=${address}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
  }, [branch]);

  // ── States ────────────────────────────────────────────────────────────────
  if (loading) return <Spinner />;

  if (error)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          style={{
            background: "#fee2e2",
            color: "#b91c1c",
            borderRadius: 12,
            padding: "16px 24px",
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      </div>
    );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        .sucursal-root { font-family: 'DM Sans', system-ui, sans-serif; background: #f6f8fb; min-height: 100vh; }
        .service-row:hover { background: #f0f6ff !important; }
        .report-row:hover { background: #f0f6ff !important; border-color: #93c5fd !important; }
        .report-btn { cursor: pointer; background: none; border: none; padding: 0; font-family: inherit; width: 100%; text-align: left; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
      `}</style>

      <div className="sucursal-root">
        {/* ── TOP BAR ─────────────────────────────────────────── */}
        <div
          style={{
            background: "#fff",
            borderBottom: "1px solid #e8edf3",
            padding: "0 32px",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: 1280,
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: 64,
              gap: 16,
            }}
          >
            {/* Left: identity */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: "#1975d1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                <Icon.Store />
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 16,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {branch?.name || "Sucursal"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#94a3b8",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Icon.Location />
                  {branch?.address || "Sin dirección"}
                </div>
              </div>
            </div>

            {/* Right: badges */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <Badge bg="#dbeafe" color="#1d4ed8">
                <Icon.Bug /> {reports.length} reportes
              </Badge>
              <Badge bg="#ede9fe" color="#6d28d9">
                <Icon.Wrench /> {services.length} servicios
              </Badge>
            </div>
          </div>
        </div>

        {/* ── PAGE BODY ───────────────────────────────────────── */}
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: "28px 32px 48px",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >

          {/* ── ROW 1: INFO + MAP ────────────────────────────── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "360px 1fr",
              gap: 20,
              alignItems: "stretch",
            }}
          >
            {/* INFO CARD */}
            <Card style={{ display: "flex", flexDirection: "column" }}>
              <SectionHeader
                icon={Icon.Store}
                title="Información de sucursal"
              />

              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {[
                  { label: "Cliente", value: branch?.client?.name },
                  { label: "Dirección", value: branch?.address },
                  {
                    label: "ID",
                    value: branch?._id || branch?.id,
                    mono: true,
                  },
                ].map(({ label, value, mono }) => (
                  <div
                    key={label}
                    style={{
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 4,
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        fontSize: mono ? 12 : 14,
                        fontWeight: 600,
                        color: "#0f172a",
                        fontFamily: mono ? "'DM Mono', monospace" : "inherit",
                        wordBreak: "break-all",
                      }}
                    >
                      {value || "—"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Stat pills */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10,
                  marginTop: 16,
                }}
              >
                {[
                  { label: "Reportes", value: reports.length, color: "#1975d1" },
                  { label: "Servicios", value: services.length, color: "#7c3aed" },
                  { label: "Nivel prom.", value: avgPestLevel, color: "#ea580c" },
                ].map(({ label, value, color }) => (
                  <div
                    key={label}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e8edf3",
                      borderRadius: 10,
                      padding: "10px 12px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 800,
                        color,
                        lineHeight: 1,
                      }}
                    >
                      {value}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#94a3b8",
                        marginTop: 3,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Upcoming */}
              {upcomingServices.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 8,
                    }}
                  >
                    Próximos servicios
                  </div>
                  {upcomingServices.map((s, i) => {
                    const st = getStatusStyle(s.status);
                    return (
                      <div
                        key={s._id || i}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 0",
                          borderBottom: i < upcomingServices.length - 1 ? "1px solid #f1f5f9" : "none",
                        }}
                      >
                        <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                          {s.type || s.name || "Servicio"}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 12, color: "#64748b" }}>
                            {fmtDate(s.date)}
                          </span>
                          <Badge bg={st.bg} color={st.text}>{st.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* MAP */}
            <Card style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "20px 24px 16px", flexShrink: 0 }}>
                <SectionHeader
                  icon={Icon.Location}
                  title="Ubicación"
                  subtitle={branch?.address}
                />
              </div>
              <div style={{ flex: 1, minHeight: 260 }}>
                <iframe
                  title="Google Maps"
                  src={googleMapsUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: "block", minHeight: 260 }}
                  loading="lazy"
                />
              </div>
            </Card>
          </div>

          {/* ── ROW 2: CHART (full width) ───────────────────── */}
          <Card>
            <SectionHeader
              icon={Icon.Chart}
              title="Tendencia de plagas"
              subtitle={`Últimos ${chartData.length} registros`}
              action={
                chartData.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  >
                    {[
                      { label: "Nulo (0)", color: "#94a3b8" },
                      { label: "Bajo (1)", color: "#16a34a" },
                      { label: "Medio (2)", color: "#ca8a04" },
                      { label: "Crítico (3)", color: "#b91c1c" },
                    ].map(({ label, color }) => (
                      <span
                        key={label}
                        style={{ display: "flex", alignItems: "center", gap: 4 }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: color,
                            display: "inline-block",
                          }}
                        />
                        {label}
                      </span>
                    ))}
                  </div>
                )
              }
            />

            {chartData.length === 0 ? (
              <div
                style={{
                  height: 240,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94a3b8",
                  fontSize: 14,
                  background: "#f8fafc",
                  borderRadius: 10,
                  border: "1px dashed #e2e8f0",
                }}
              >
                No hay datos de plagas registrados
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 16, bottom: 0, left: -12 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="fecha"
                    tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "DM Sans" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 3]}
                    ticks={[0, 1, 2, 3]}
                    tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "DM Sans" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <ReferenceLine y={1} stroke="#16a34a" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: "Bajo", position: "right", fontSize: 10, fill: "#15803d" }} />
                  <ReferenceLine y={2} stroke="#ca8a04" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: "Medio", position: "right", fontSize: 10, fill: "#a16207" }} />
                  <ReferenceLine y={3} stroke="#b91c1c" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: "Crítico", position: "right", fontSize: 10, fill: "#991b1b" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="nivel"
                    stroke="#1975d1"
                    strokeWidth={2.5}
                    dot={{ fill: "#1975d1", r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* ── ROW 3: REPORTS + SERVICE HISTORY ────────────── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "400px 1fr",
              gap: 20,
              alignItems: "stretch",
            }}
          >
            {/* RECENT REPORTS */}
            <Card style={{ display: "flex", flexDirection: "column" }}>
              <SectionHeader
                icon={Icon.Report}
                title="Últimos reportes"
                subtitle={`${reports.length} total`}
              />

              {latestReports.length === 0 ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontSize: 13,
                    padding: "32px 0",
                  }}
                >
                  Sin reportes disponibles
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    flex: 1,
                    overflowY: "auto",
                  }}
                >
                  {latestReports.map((report, index) => {
                    const svc = report._service;
                    const { bg, text, label: lvlLabel } = getPestLevelColor(
                      report.pestLevel
                    );
                    const serviceDate = fmtDate(svc?.date);
                    const serviceType = svc?.type || svc?.name || "Servicio";

                    return (
                      <button
                        key={report._id || index}
                        className="report-btn"
                        onClick={() => {
                          if (svc) {
                            setSelectedService(svc);
                            setReportModalOpen(true);
                          }
                        }}
                        disabled={!svc}
                      >
                        <div
                          className="report-row"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 12px",
                            borderRadius: 10,
                            border: "1px solid #e8edf3",
                            background: "#fafcff",
                            transition: "all 0.15s",
                            gap: 10,
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: "#0f172a",
                                marginBottom: 2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {serviceType}
                            </div>
                            <div
                              style={{
                                fontSize: 12,
                                color: "#64748b",
                              }}
                            >
                              {serviceDate}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexShrink: 0,
                            }}
                          >
                            <Badge bg={bg} color={text}>
                              Niv. {report.pestLevel ?? 0}
                            </Badge>
                            {svc && (
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 6,
                                  background: "#eff6ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#1975d1",
                                }}
                              >
                                <Icon.ExternalLink />
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* SERVICE HISTORY */}
            <Card style={{ display: "flex", flexDirection: "column" }}>
              <SectionHeader
                icon={Icon.History}
                title="Historial de servicios"
                subtitle={`${servicesHistory.length} servicios realizados`}
              />

              {servicesHistory.length === 0 ? (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    fontSize: 13,
                    padding: "32px 0",
                  }}
                >
                  Sin historial disponible
                </div>
              ) : (
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {/* Table header */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 140px 110px 90px",
                      gap: 12,
                      padding: "8px 12px",
                      background: "#f8fafc",
                      borderRadius: 8,
                      marginBottom: 4,
                    }}
                  >
                    {["Servicio", "Fecha", "Hora", "Estado"].map((h) => (
                      <div
                        key={h}
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {h}
                      </div>
                    ))}
                  </div>

                  {servicesHistory.map((service, index) => {
                    const st = getStatusStyle(service.status);
                    return (
                      <div
                        key={service._id || index}
                        className="service-row"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 140px 110px 90px",
                          gap: 12,
                          padding: "11px 12px",
                          borderRadius: 8,
                          borderBottom:
                            index < servicesHistory.length - 1
                              ? "1px solid #f1f5f9"
                              : "none",
                          transition: "background 0.15s",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: "#0f172a",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {service.type || service.name || "Servicio"}
                        </div>
                        <div style={{ fontSize: 13, color: "#475569" }}>
                          {fmtDate(service.date)}
                        </div>
                        <div style={{ fontSize: 13, color: "#475569" }}>
                          {service.hour || "—"}
                        </div>
                        <div>
                          <Badge bg={st.bg} color={st.text}>
                            {st.label}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* ── ROW 4: GALLERY (disabled) ────────────────────── */}
          <GalleryRow
            branchId={id}
          />
        </div>
      </div>

      {/* ── MODAL ───────────────────────────────────────────── */}
      {selectedService && (
        <ViewPestReportModal
          open={reportModalOpen}
          service={selectedService}
          onClose={() => {
            setReportModalOpen(false);
            setSelectedService(null);
          }}
        />
      )}
    </>
  );
}